import React, { useState, useEffect, useRef, useCallback } from 'react';
import { movimentacaoEstoqueService, lojasService } from '../../services/api';
import DraggableWindow from './DraggableWindow';

// Operador gravado em scekarde.CDOPE/CDFUN — sempre 13 (regra do negócio).
const OPERADOR_FIXO = 13;

const OPERACOES = [
  { value: 'ENTRADA', label: 'Entrada (+)' },
  { value: 'SAIDA', label: 'Saída (−)' },
  { value: 'PERDA', label: 'Perda / Quebra (−)' },
  { value: 'AJUSTE', label: 'Ajuste de inventário (= saldo final)' },
];

const WindowMovimentacaoEstoque = ({ onClose, onMinimize, isMinimized, zIndex, onFocus, userData }) => {
  const [lojas, setLojas] = useState([]);
  const [filial, setFilial] = useState(userData?.loja_id || 22);
  const [codigo, setCodigo] = useState('');
  const [produto, setProduto] = useState(null);      // { CDPRODU, NOME, ESTOQ, ENDER }
  const [operacao, setOperacao] = useState('ENTRADA');
  const [qtd, setQtd] = useState('');
  const [saldoFinal, setSaldoFinal] = useState('');   // só para AJUSTE
  const [lote, setLote] = useState('');
  const [doc, setDoc] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [historico, setHistorico] = useState([]);     // lançamentos feitos nesta sessão
  const codigoRef = useRef(null);

  const isAjuste = operacao === 'AJUSTE';

  useEffect(() => {
    lojasService.getLojas()
      .then((l) => setLojas((l || []).sort((a, b) => a.numero - b.numero)))
      .catch(() => {});
  }, []);

  const buscarProduto = useCallback(async () => {
    const cod = String(codigo).trim();
    if (!cod) return;
    setCarregando(true);
    setErro('');
    setProduto(null);
    try {
      const p = await movimentacaoEstoqueService.buscarSaldo(cod, filial);
      if (!p) {
        setErro('Produto não encontrado nesta filial.');
      } else {
        setProduto(p);
        setSaldoFinal(String(p.ESTOQ ?? 0));
      }
    } catch (e) {
      setErro(e.message || 'Erro ao buscar produto.');
    } finally {
      setCarregando(false);
    }
  }, [codigo, filial]);

  const registrar = async () => {
    if (!produto) { setErro('Busque um produto primeiro.'); return; }

    const opLabel = OPERACOES.find((o) => o.value === operacao)?.label || operacao;
    // Operador fixo nas movimentações (regra do negócio): sempre 13
    const operador = OPERADOR_FIXO;
    let payload = {
      cdprodu: produto.CDPRODU,
      cdfil: parseInt(filial, 10),
      operacao,
      lote,
      doc,
      cdfun: operador,
      cdope: operador,
    };

    let resumo;
    if (isAjuste) {
      const sf = Number(saldoFinal);
      if (!Number.isFinite(sf)) { setErro('Informe o saldo final.'); return; }
      payload.ajusteParaSaldo = sf;
      resumo = `Ajustar saldo de ${produto.ESTOQ} para ${sf}`;
    } else {
      const q = Number(qtd);
      if (!Number.isFinite(q) || q <= 0) { setErro('Informe uma quantidade maior que zero.'); return; }
      payload.qtd = q;
      resumo = `${opLabel} de ${q} un.`;
    }

    if (!window.confirm(
      `Confirmar movimentação?\n\nProduto: ${produto.CDPRODU} - ${produto.NOME}\n` +
      `Filial: ${filial}\n${resumo}\n\nIsto altera o estoque real.`
    )) return;

    setSalvando(true);
    setErro('');
    try {
      const r = await movimentacaoEstoqueService.registrar(payload);
      setHistorico((h) => [{
        ts: new Date().toLocaleTimeString(),
        cdprodu: produto.CDPRODU,
        nome: produto.NOME,
        operacao: opLabel,
        saldoAnterior: r.saldoAnterior,
        saldoNovo: r.saldoNovo,
      }, ...h]);
      // atualiza o saldo exibido e limpa os campos de quantidade
      setProduto((p) => ({ ...p, ESTOQ: r.saldoNovo }));
      setSaldoFinal(String(r.saldoNovo));
      setQtd('');
      setLote('');
      setDoc('');
    } catch (e) {
      setErro(e.message || 'Erro ao registrar movimentação.');
    } finally {
      setSalvando(false);
    }
  };

  const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 };
  const inp = { width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' };
  const row = { display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' };

  return (
    <DraggableWindow
      title="Movimentação de Estoque"
      icon="fa-dolly"
      onClose={onClose}
      onMinimize={onMinimize}
      isMinimized={isMinimized}
      zIndex={zIndex}
      onFocus={onFocus}
      initialWidth={620}
    >
      <div style={{ padding: 16 }}>
        {/* Filial + código */}
        <div style={row}>
          <div style={{ width: 180 }}>
            <label style={lbl}>Filial</label>
            <select style={inp} value={filial} onChange={(e) => { setFilial(e.target.value); setProduto(null); }}>
              {lojas.length === 0 && <option value={filial}>{filial}</option>}
              {lojas.map((l) => (
                <option key={l.numero} value={l.numero}>{l.numero} - {l.nome}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={lbl}>Código / Código de barras</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                ref={codigoRef}
                style={inp}
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); buscarProduto(); } }}
                placeholder="Digite e Enter"
                autoFocus
              />
              <button onClick={buscarProduto} disabled={carregando}
                style={{ padding: '8px 14px', border: 'none', borderRadius: 6, background: '#0078d7', color: '#fff', cursor: 'pointer' }}>
                {carregando ? '...' : 'Buscar'}
              </button>
            </div>
          </div>
        </div>

        {/* Produto encontrado */}
        {produto && (
          <div style={{ background: '#f1f5f9', borderRadius: 8, padding: 12, marginBottom: 12 }}>
            <div style={{ fontWeight: 700, color: '#0f172a' }}>{produto.CDPRODU} — {produto.NOME}</div>
            <div style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>
              Saldo atual: <strong>{produto.ESTOQ}</strong>
              {produto.ENDER ? <> &nbsp;•&nbsp; Endereço: {produto.ENDER}</> : null}
            </div>
          </div>
        )}

        {/* Operação + quantidade */}
        <div style={row}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label style={lbl}>Operação</label>
            <select style={inp} value={operacao} onChange={(e) => setOperacao(e.target.value)}>
              {OPERACOES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {isAjuste ? (
            <div style={{ width: 160 }}>
              <label style={lbl}>Saldo final</label>
              <input style={inp} type="number" value={saldoFinal} onChange={(e) => setSaldoFinal(e.target.value)} />
            </div>
          ) : (
            <div style={{ width: 160 }}>
              <label style={lbl}>Quantidade</label>
              <input style={inp} type="number" min="0" value={qtd} onChange={(e) => setQtd(e.target.value)} />
            </div>
          )}
        </div>

        <div style={row}>
          <div style={{ width: 200 }}>
            <label style={lbl}>Lote (opcional)</label>
            <input style={inp} value={lote} onChange={(e) => setLote(e.target.value)} />
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label style={lbl}>Documento / Obs (opcional)</label>
            <input style={inp} value={doc} onChange={(e) => setDoc(e.target.value)} />
          </div>
        </div>

        {erro && (
          <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
            <i className="fas fa-triangle-exclamation" /> {erro}
          </div>
        )}

        <button
          onClick={registrar}
          disabled={!produto || salvando}
          style={{
            width: '100%', padding: '12px', border: 'none', borderRadius: 8,
            background: produto && !salvando ? '#16a34a' : '#94a3b8',
            color: '#fff', fontWeight: 700, fontSize: 15,
            cursor: produto && !salvando ? 'pointer' : 'not-allowed'
          }}>
          {salvando ? 'Registrando...' : 'Registrar Movimentação'}
        </button>

        {/* Histórico da sessão */}
        {historico.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>
              Lançamentos desta sessão
            </div>
            <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 6 }}>
              {historico.map((h, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', borderBottom: '1px solid #f1f5f9', fontSize: 12 }}>
                  <span>{h.ts} · {h.cdprodu} {h.nome?.slice(0, 22)}</span>
                  <span style={{ fontWeight: 600 }}>{h.operacao}: {h.saldoAnterior} → {h.saldoNovo}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DraggableWindow>
  );
};

export default WindowMovimentacaoEstoque;
